import React, {useCallback} from 'react';

import {NumberInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import CheckBox from '@pb/kit-component-checkbox';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {BrowserLimitsSkeleton} from './browser-limits.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {BrowserRegistrationSettings} from 'src/openapi/admin/models/BrowserRegistrationSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    browserLimits: BrowserRegistrationSettings,
    setBrowserLimits: React.Dispatch<React.SetStateAction<BrowserRegistrationSettings>>,
    disabled?: boolean,
    onFieldChange: (parameterName: string) => void,
};

const SECTION_NAME = 'Browser Limits';

export const BrowserLimitsSection = (props: Props) => {
    const {
        isLoading,
        browserLimits,
        setBrowserLimits,
        onFieldChange,
        loadingError,
        disabled,
    } = props;

    const onRegistrationEnforcedChange = useCallback(({event}: CheckBoxChangeData) => {
        onFieldChange('Enable device registration for this user');
        const registrationEnforced = event.currentTarget.checked;

        setBrowserLimits(state => ({...state, registrationEnforced}));
    }, [onFieldChange, setBrowserLimits]);

    const onMaxAllowedPlacesChange = useCallback((maxAllowedPlaces?: number) => {
        onFieldChange('Registered browser limit');
        setBrowserLimits(state => ({
            ...state,
            maxAllowedPlaces: maxAllowedPlaces || 1,
        }));
    }, [onFieldChange, setBrowserLimits]);

    return (
        <Skeleton shown={isLoading} view={<BrowserLimitsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                <FormSection headline={SECTION_NAME}>
                    <Group direction="v" alignItems="start">
                        <CheckBox
                            label="Enable device registration for this user"
                            checked={browserLimits.registrationEnforced}
                            onCheckBoxChange={onRegistrationEnforcedChange}
                            disabled={disabled}
                        />
                        <div>
                            <Label>Registered browser limit</Label>
                            <NumberInput
                                width={85}
                                minValue={1}
                                maxValue={9}
                                value={browserLimits.maxAllowedPlaces}
                                onBlur={onMaxAllowedPlacesChange}
                                readOnly={disabled}
                                disabled={!browserLimits.registrationEnforced}
                                displayedValue={browserLimits.registrationEnforced
                                    ? browserLimits.maxAllowedPlaces.toString()
                                    : ''}
                            />
                        </div>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
